import { useMemo, useEffect } from 'react';
import { Spinner } from '../common/Spinner';
import { SpinWheel } from './SpinWheel';
import { useOrganizerCandidates } from '../../hooks/useTeamEvents';
import type { OrganizerCandidate } from '../../services/api/teamEvents';

interface SpinWheelModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventTypeId: number;
  excludeUserIds: number[];
  onSelect: (user: OrganizerCandidate) => void;
  eventDate?: string;
  eventId?: number;
}

export function SpinWheelModal({
  isOpen,
  onClose,
  eventTypeId,
  excludeUserIds,
  onSelect,
  eventDate,
  eventId,
}: SpinWheelModalProps) {
  const { data: candidates, isLoading } = useOrganizerCandidates(isOpen ? eventTypeId : null, eventDate, eventId);

  const filtered = useMemo(
    () => (candidates ?? []).filter((c) => !excludeUserIds.includes(c.id)),
    [candidates, excludeUserIds]
  );

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full w-10 h-10 flex items-center justify-center text-xl transition-colors"
        aria-label="Close"
      >
        &times;
      </button>
      {isLoading ? (
        <Spinner />
      ) : (
        <SpinWheel candidates={filtered} onSelect={onSelect} />
      )}
    </div>
  );
}
